// Consent Note Dialog — WhatsApp Business compliance.
// mode 'resubscribe': opting an unsubscribed contact back in needs a written
//   record of the customer's request.
// mode 'marketing': recording marketing consent (required for MARKETING
//   templates) needs a written record of how/when the contacts agreed.
// Either way the note is saved on each contact and in the audit log.

import { useId, useRef, useState } from 'react';
import { useI18n } from '../../lib/i18n';
import { useDialogA11y } from '../../lib/useDialogA11y';

export type ConsentMode = 'resubscribe' | 'marketing';

export interface ConsentNoteDialogProps {
  isOpen: boolean;
  onClose: () => void;
  targetCount: number;
  onConfirm: (consentNote: string) => Promise<void>;
  mode?: ConsentMode;
}

const MIN_NOTE = 10;

export default function ConsentNoteDialog({
  isOpen,
  onClose,
  targetCount,
  onConfirm,
  mode = 'resubscribe',
}: ConsentNoteDialogProps) {
  const { t } = useI18n();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useDialogA11y(isOpen, panelRef, onClose);

  if (!isOpen) return null;

  const k = mode === 'marketing' ? 'subs.mconsent' : 'subs.reconsent';
  const tooShort = note.trim().length < MIN_NOTE;

  async function handleSubmit() {
    const trimmed = note.trim();
    if (trimmed.length < MIN_NOTE) {
      setError(t(`${k}.errShort`));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onConfirm(trimmed);
      setNote('');
      onClose();
    } catch (err) {
      setError((err as Error).message || t(`${k}.errFailed`));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl animate-in fade-in zoom-in-95 duration-200 dark:bg-slate-900"
      >
        <div className="border-b border-slate-100 p-5 bg-gradient-to-r from-amber-50 to-orange-50 dark:border-slate-800 dark:from-amber-950/40 dark:to-orange-950/40">
          <div className="flex items-center gap-3">
            <span className="grid h-9 w-9 place-items-center rounded-xl bg-amber-500 text-white font-bold text-lg" aria-hidden>
              🛡️
            </span>
            <div>
              <h3 id={titleId} className="text-sm font-bold text-slate-900 dark:text-slate-100">
                {t(`${k}.title`)}
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {t(`${k}.count`, { count: targetCount })}
              </p>
            </div>
          </div>
        </div>

        <div className="p-5 space-y-4">
          <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-3 text-xs leading-relaxed text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
            {t(`${k}.warning`)}
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
              {t(`${k}.label`)} <span className="text-red-600">*</span>
            </label>
            <textarea
              value={note}
              rows={3}
              placeholder={t(`${k}.placeholder`)}
              onChange={(e) => setNote(e.target.value)}
              className="w-full rounded-lg border border-slate-200 p-2.5 text-xs outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
            />
            <p className="text-[10px] text-slate-400 mt-1">{t('subs.consentCommon.recorded')}</p>
          </div>

          {error && <p className="text-xs font-semibold text-red-600">⚠️ {error}</p>}

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="rounded-lg border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={busy || tooShort}
              className="rounded-lg bg-amber-600 px-4 py-2 text-xs font-bold text-white shadow-sm hover:bg-amber-700 disabled:opacity-50"
            >
              {busy ? t('subs.consentCommon.saving') : t(`${k}.confirm`)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
